/**
 * FlaggedQuestionsPage — the "review later" screen.
 *
 * Lists every question the user flagged during a session (via the bookmark
 * toggle on the question card), revealed with its answer + explanation, plus
 * an editable note for their own exam tip / why it was tricky. Distinct from
 * the SM-2 spaced-repetition ReviewPage: these are manual, explicit marks.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/ui';
import { Card } from '../components/ui';
import QuestionCard from '../components/QuestionCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  fetchFlaggedQuestions,
  unflagQuestion,
  updateFlagNote,
  type FlaggedQuestion,
} from '../api/flagged';

/* ── Per-question note editor (autosaves on blur) ───────────────────────── */
function NoteEditor({ questionId, initial }: { questionId: number; initial: string | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initial ?? '');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: (note: string) => updateFlagNote(questionId, note.trim() ? note.trim() : null),
    onSuccess: () => {
      setSavedAt(Date.now());
      void queryClient.invalidateQueries({ queryKey: ['flagged'] });
    },
  });

  const dirty = (value.trim() || '') !== (initial ?? '');

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/25 dark:bg-amber-500/10">
      <label htmlFor={`note-${questionId}`} className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-amber-700 dark:text-amber-300">
        {t('study.flagged.noteLabel')}
      </label>
      <textarea
        id={`note-${questionId}`}
        value={value}
        onChange={(e) => { setValue(e.target.value); setSavedAt(null); }}
        onBlur={() => { if (dirty) save.mutate(value); }}
        rows={2}
        maxLength={2000}
        placeholder={t('study.flagged.notePlaceholder')}
        className="w-full resize-y rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-amber-400 dark:border-amber-500/30 dark:bg-card-dark dark:text-slate-200"
      />
      {savedAt && !dirty && (
        <span className="mt-1 block text-[11px] font-semibold text-green-600 dark:text-green-400">{t('study.flagged.noteSaved')}</span>
      )}
    </div>
  );
}

/* ── One flagged question (revealed card + note + remove) ───────────────── */
function FlaggedItem({ q }: { q: FlaggedQuestion }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => unflagQuestion(q.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flagged'] });
      void queryClient.invalidateQueries({ queryKey: ['flagged-ids'] });
    },
  });

  return (
    <div>
      <QuestionCard
        question={q}
        showAnswer
        selectedAnswer={null}
        onSelectOption={() => {}}
        isFlagged
        onToggleFlag={() => remove.mutate()}
      />
      <NoteEditor questionId={q.id} initial={q.note} />
    </div>
  );
}

export default function FlaggedQuestionsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('study.flagged.title'));

  const { data, isLoading } = useQuery({
    queryKey: ['flagged'],
    queryFn: fetchFlaggedQuestions,
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <AppShell>
      <div className="mx-auto flex w-full max-w-[880px] flex-col gap-6">
        <div>
          <span className="text-[11px] font-extrabold uppercase tracking-[0.13em] text-amber-600 dark:text-amber-400">
            {t('study.flagged.kicker')}
          </span>
          <h1 className="mt-2 text-[28px] font-extrabold leading-[1.1] tracking-[-0.03em] text-slate-900 dark:text-white">
            {t('study.flagged.title')}
          </h1>
          <p className="mt-2 text-[13.5px] leading-snug text-slate-600 dark:text-slate-300">
            {t('study.flagged.sub')}
          </p>
        </div>

        {isLoading ? (
          <Card className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">{t('study.flagged.loading')}</Card>
        ) : items.length === 0 ? (
          <Card className="flex flex-col items-center gap-3 p-10 text-center">
            <svg className="h-9 w-9 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z" />
            </svg>
            <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">{t('study.flagged.empty')}</p>
          </Card>
        ) : (
          <div className="flex flex-col gap-6">
            {items.map((q) => (
              <FlaggedItem key={q.id} q={q} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
